import React, { useState } from 'react';
import { useNotify } from 'react-admin';
import { useBrsCreateStyles } from '../BrsCreateStyles';
import { IsciEntryCard } from './IsciEntryCard';
import { HybridModeBanner } from './HybridModeBanner';
import { BulkAddModal } from './BulkAddModal';

// Normalize an ISCI for comparison and storage. Backend / lookups expect the
// trailing 'H' suffix and uppercase letters.
const normalizeIsci = (raw) => {
  const trimmed = String(raw || '').trim().toUpperCase();
  if (!trimmed) return '';
  return trimmed.endsWith('H') ? trimmed : `${trimmed}H`;
};

const existingIscis = (spots) =>
  new Set(
    spots
      .map((s) => (s.isciasset?.isci || '').trim().toUpperCase())
      .filter(Boolean),
  );

export const BroadcastRequestsSection = ({
  spots,
  onSpotsChange,
  unitRotation,
  brandChoices,
  createSpot,
  flightStartDate,
  flightEndDate,
}) => {
  const classes = useBrsCreateStyles();
  const notify = useNotify();
  const [searchIsci, setSearchIsci] = useState('');
  const [bulkAddOpen, setBulkAddOpen] = useState(false);

  // Use functional setState so concurrent CatDV lookups from N freshly
  // bulk-added spots don't stomp each other's classification/metadata.
  // Without this, lookups racing in parallel each capture a stale `spots`
  // snapshot and the last write wins — which is why bulk-added ISCIs only
  // ever showed the NEW badge even when they exist in Cadent.
  const handleSpotChange = (index, updatedSpot) => {
    onSpotsChange((prev) => {
      if (!Array.isArray(prev)) return prev;
      const next = [...prev];
      next[index] = { ...updatedSpot, unit_rotation_type: unitRotation };
      return next;
    });
  };

  const handleRemoveSpot = (index) => {
    onSpotsChange((prev) => {
      if (!Array.isArray(prev) || prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    onSpotsChange((prev) => {
      if (!Array.isArray(prev)) return prev;
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const handleMoveDown = (index) => {
    onSpotsChange((prev) => {
      if (!Array.isArray(prev) || index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const addEmptySpot = () => {
    const scheduleDefault = unitRotation === 'unit' ? 1 : 100;
    onSpotsChange((prev) => [...prev, createSpot({ unit_rotation_type: unitRotation }, scheduleDefault)]);
  };

  const handleSearchAdd = () => {
    const isci = normalizeIsci(searchIsci);
    if (!isci) return;
    const present = existingIscis(spots);
    if (present.has(isci)) {
      notify(`${isci} is already in the list`, 'info');
      setSearchIsci('');
      return;
    }
    const scheduleDefault = unitRotation === 'unit' ? 1 : 100;
    onSpotsChange((prev) => [
      ...prev,
      createSpot({
        isciasset: {
          isci,
          sigma_encoding: 'CADENT',
          bvs_encoding: 'NO',
          include_sd: false,
          original_iscis: [{}],
        },
        unit_rotation_type: unitRotation,
      }, scheduleDefault),
    ]);
    setSearchIsci('');
  };

  const handleBulkAdd = (iscis) => {
    const present = existingIscis(spots);
    const seenInBatch = new Set();
    const fresh = [];
    let skipped = 0;
    const scheduleDefault = unitRotation === 'unit' ? 1 : 100;
    for (const raw of iscis) {
      const isci = normalizeIsci(raw);
      if (!isci) continue;
      if (present.has(isci) || seenInBatch.has(isci)) {
        skipped += 1;
        continue;
      }
      seenInBatch.add(isci);
      fresh.push(
        createSpot({
          isciasset: {
            isci,
            sigma_encoding: 'CADENT',
            bvs_encoding: 'NO',
            include_sd: false,
            original_iscis: [{}],
          },
          unit_rotation_type: unitRotation,
        }, scheduleDefault),
      );
    }
    if (fresh.length === 0 && skipped === 0) return;
    if (fresh.length > 0) {
      onSpotsChange((prev) => [...prev, ...fresh]);
    }
    if (skipped > 0) {
      notify(
        `Skipped ${skipped} duplicate ISCI${skipped === 1 ? '' : 's'}`,
        'info',
      );
    }
  };

  return (
    <>
      <div className={classes.broadcastHeader}>
        <h3 className={classes.broadcastTitle}>Broadcast Requests</h3>
        <div className={classes.broadcastActions}>
          <input
            className={classes.formInput}
            style={{ width: 200 }}
            placeholder="Search for ISCI"
            value={searchIsci}
            onChange={(e) => setSearchIsci(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchAdd()}
          />
          <button
            className={`${classes.btn} ${classes.btnGhost}`}
            style={{ fontSize: 12 }}
            onClick={handleSearchAdd}
            type="button"
          >
            ADD
          </button>
          <button
            className={`${classes.btn} ${classes.btnBulk}`}
            style={{ fontSize: 12 }}
            onClick={() => setBulkAddOpen(true)}
            type="button"
          >
            BULK ADD
          </button>
        </div>
      </div>

      <HybridModeBanner spots={spots} />

      {spots.map((spot, index) => {
        const siblingIscis = new Set(
          spots
            .filter((_, i) => i !== index)
            .map((s) => (s.isciasset?.isci || '').trim().toUpperCase())
            .filter(Boolean),
        );
        return (
          <IsciEntryCard
            key={spot._id}
            index={index}
            spot={{ ...spot, unit_rotation_type: unitRotation }}
            onChange={handleSpotChange}
            onRemove={handleRemoveSpot}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            isFirst={index === 0}
            isLast={index === spots.length - 1}
            brandChoices={brandChoices}
            unitRotation={unitRotation}
            siblingIscis={siblingIscis}
            flightStartDate={flightStartDate}
            flightEndDate={flightEndDate}
          />
        );
      })}

      <div className={classes.addIsciButton}>
        <button
          className={`${classes.btn} ${classes.btnGhost} ${classes.addIsciFullWidth}`}
          onClick={addEmptySpot}
          type="button"
        >
          &#8853; ADD ISCI
        </button>
        <div className={classes.addIsciHint}>
          Add ISCIs individually, or multiple ISCIs using the Bulk Add
        </div>
      </div>

      <BulkAddModal open={bulkAddOpen} onClose={() => setBulkAddOpen(false)} onAdd={handleBulkAdd} />
    </>
  );
};
